import { useTranslation } from 'react-i18next';
import { Alert, Confirmation, Modal } from '@otp/ui';
import {
  UpdateWttpQuestionDocument,
  WttpQuestion,
  WttpQuestionByIdDocument,
  WttpQuestionByIdQuery,
  WttpQuestionsDocument,
  WttpQuestionStatus,
} from 'src/graphql/generated/graphql';
import React from 'react';
import { QuestionForm } from 'src/components/wttp/questions/form';
import { useMutation, useQuery } from '@apollo/client';
import { WttpTemplateAction } from 'src/components/wttp/templates/list/table/columns/actions/actions-modals';

export enum WttpQuestionAction {
  Edit = 'edit',
  Clone = 'clone',
  Archive = 'archive',
  Unarchive = 'unarchive',
  Activate = 'activate',
  Deactivate = 'deactivate',
  ArchiveAlert = 'archive-alert',
}

export const ActionModals = ({
  action,
  onClose,
  question,
  onActionFinished,
}: {
  action: WttpQuestionAction;
  onClose: () => void;
  onSubmitted?: (action: WttpQuestionAction) => void;
  question: Partial<WttpQuestion>;
  onActionFinished?: (action: WttpQuestionAction) => void;
}) => {
  const { t } = useTranslation();
  const [extendedQuestion, setExtendedQuestion] = React.useState(question);

  const [updateQuestion] = useMutation(UpdateWttpQuestionDocument, {
    refetchQueries: [WttpQuestionsDocument],
  });

  useQuery<WttpQuestionByIdQuery>(WttpQuestionByIdDocument, {
    variables: {
      wttpQuestionId: question.id,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (action === WttpQuestionAction.Clone) {
        return setExtendedQuestion({
          ...question,
          ...data?.wttpQuestionById,
          parentId: extendedQuestion.id,
          id: undefined,
          fileAssets: data?.wttpQuestionById?.fileAssets?.map((file) => ({
            ...file,
            isParentAsset: true,
          })),
        } as Partial<WttpQuestion>);
      }

      setExtendedQuestion({
        ...question,
        ...data?.wttpQuestionById,
      } as Partial<WttpQuestion>);
    },
  });

  const onFinished = () => {
    onActionFinished?.(action);
    onClose();
  };

  const onArchiveQuestionToggle = async (isActive: boolean) => {
    await updateQuestion({
      variables: {
        data: {
          isActive,
        },
        id: question.id,
      },
    });
    onClose();
  };

  const onActivateQuestionToggle = async (status: WttpQuestionStatus) => {
    await updateQuestion({
      variables: {
        data: {
          status,
        },
        id: question.id,
      },
    });
    onClose();
  };

  return (
    <>
      {action === WttpQuestionAction.Edit && (
        <QuestionForm
          title={t('wttp.question.form.edit-question')}
          question={extendedQuestion}
          onFinish={onFinished}
          refetchQueries={[WttpQuestionsDocument]}
          onClose={onClose}
        />
      )}
      {action === WttpQuestionAction.Clone && (
        <QuestionForm
          title={t('wttp.question.form.clone-question')}
          question={extendedQuestion}
          onFinish={onFinished}
          refetchQueries={[WttpQuestionsDocument]}
          isClone
          onClose={onClose}
        />
      )}
      {action === WttpQuestionAction.Archive && (
        <Confirmation
          open
          onClose={onClose}
          onConfirm={() => onArchiveQuestionToggle(false)}
          message={t('wttp.question.archive-confirm')}
        />
      )}
      {action === WttpQuestionAction.Unarchive && (
        <Confirmation
          open
          onClose={onClose}
          onConfirm={() => onArchiveQuestionToggle(true)}
          message={t('wttp.question.unarchive-confirm')}
        />
      )}
      {action === WttpQuestionAction.Activate && (
        <Confirmation
          open
          onClose={onClose}
          onConfirm={() => onActivateQuestionToggle(WttpQuestionStatus.Active)}
          message={t('wttp.question.activate-confirm')}
        />
      )}
      {action === WttpQuestionAction.Deactivate && (
        <Confirmation
          open
          onClose={onClose}
          onConfirm={() => onActivateQuestionToggle(WttpQuestionStatus.Draft)}
          message={t('wttp.question.deactivate-confirm')}
        />
      )}
      {action === WttpQuestionAction.ArchiveAlert && (
        <Alert
          open
          onClose={onClose}
          title={t('wttp.question.archive-alert-title')}
          message={t('wttp.question.archive-alert-description')}
        />
      )}
    </>
  );
};
